/**
 * Conversion orchestrator for PMC XML and arXiv HTML to Markdown.
 *
 * Ties together the parsers and Markdown writer with file I/O.
 */
import { readFile, stat, writeFile } from "node:fs/promises";
import { parseArxivHtml } from "./arxiv-html-parser.js";
import { parseJatsBackMatter, parseJatsBody, parseJatsMetadata, parseJatsReferences, } from "./jats-parser.js";
import { writeMarkdown } from "./markdown-writer.js";
/**
 * Convert a PMC JATS XML file to Markdown.
 *
 * Reads the XML, parses it into a JatsDocument, writes Markdown,
 * and optionally updates meta.json.
 */
export async function convertPmcXmlToMarkdown(xmlPath, mdPath, metaPath) {
    try {
        const xml = await readFile(xmlPath, "utf-8");
        // Parse
        const metadata = parseJatsMetadata(xml);
        const sections = parseJatsBody(xml);
        const references = parseJatsReferences(xml);
        const backMatter = parseJatsBackMatter(xml);
        const doc = { metadata, sections, references };
        if (backMatter.acknowledgments)
            doc.acknowledgments = backMatter.acknowledgments;
        if (backMatter.appendices)
            doc.appendices = backMatter.appendices;
        if (backMatter.footnotes)
            doc.footnotes = backMatter.footnotes;
        if (backMatter.floats)
            doc.floats = backMatter.floats;
        if (backMatter.notes)
            doc.notes = backMatter.notes;
        // Write Markdown
        const md = writeMarkdown(doc);
        await writeFile(mdPath, md, "utf-8");
        // Update meta.json if path provided and file exists
        if (metaPath) {
            try {
                await stat(metaPath);
                const metaRaw = await readFile(metaPath, "utf-8");
                const meta = JSON.parse(metaRaw);
                const mdStat = await stat(mdPath);
                meta.files.markdown = {
                    filename: "fulltext.md",
                    source: "conversion",
                    retrievedAt: new Date().toISOString(),
                    size: mdStat.size,
                    convertedFrom: "fulltext.xml",
                };
                await writeFile(metaPath, `${JSON.stringify(meta, null, 2)}\n`, "utf-8");
            }
            catch {
                // meta.json doesn't exist or can't be read, skip update
            }
        }
        const result = { success: true };
        result.title = metadata.title;
        result.sections = sections.length;
        result.references = references.length;
        return result;
    }
    catch (err) {
        const result = { success: false };
        result.error = err instanceof Error ? err.message : String(err);
        return result;
    }
}
/**
 * Convert an arXiv HTML file to Markdown.
 *
 * Reads the HTML, parses it into a JatsDocument via the arXiv HTML parser,
 * writes Markdown using the shared writer, and optionally updates meta.json.
 */
export async function convertArxivHtmlToMarkdown(htmlPath, mdPath, metaPath) {
    try {
        const html = await readFile(htmlPath, "utf-8");
        const doc = parseArxivHtml(html);
        // Write Markdown
        const md = writeMarkdown(doc);
        await writeFile(mdPath, md, "utf-8");
        // Update meta.json if path provided and file exists
        if (metaPath) {
            try {
                await stat(metaPath);
                const metaRaw = await readFile(metaPath, "utf-8");
                const meta = JSON.parse(metaRaw);
                const mdStat = await stat(mdPath);
                meta.files.markdown = {
                    filename: "fulltext.md",
                    source: "conversion",
                    retrievedAt: new Date().toISOString(),
                    size: mdStat.size,
                    convertedFrom: "fulltext.html",
                };
                await writeFile(metaPath, `${JSON.stringify(meta, null, 2)}\n`, "utf-8");
            }
            catch {
                // meta.json doesn't exist or can't be read, skip update
            }
        }
        const result = { success: true };
        result.title = doc.metadata.title;
        result.sections = doc.sections.length;
        result.references = doc.references.length;
        return result;
    }
    catch (err) {
        const result = { success: false };
        result.error = err instanceof Error ? err.message : String(err);
        return result;
    }
}
//# sourceMappingURL=index.js.map